/**
 * Vidéos animées du diaporama (section slideshow) et de la bannière double.
 * - La vidéo démarre au début chaque fois que sa diapositive s'affiche, et s'arrête quand on la quitte.
 * - data-end sur [data-slide-video] règle la fin :
 *     loop   : rejouer en boucle ;
 *     freeze : rester sur la dernière image ;
 *     tail   : rejouer en boucle les data-tail dernières secondes.
 * - Hors diaporama (bannière double) : la vidéo joue quand elle est à l'écran et reprend où
 *   elle était ; seule celle qui s'affiche (ordinateur ou mobile) se charge.
 * - La vidéo joue même quand l'ordinateur demande moins d'animations (Windows avec « Afficher
 *   les animations » désactivé, par exemple) : un bouton pause / lecture permet de l'arrêter,
 *   comme chez Banana Republic. Un arrêt demandé par le visiteur est respecté ensuite.
 */
(() => {
  // Plusieurs sections peuvent inclure ce fichier : une seule copie suffit.
  if (window.pentagoneSlideVideo) return;
  window.pentagoneSlideVideo = true;

  const ICON_PAUSE = '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><rect x="2" y="1" width="3.5" height="12"/><rect x="8.5" y="1" width="3.5" height="12"/></svg>';
  const ICON_PLAY = '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M3 1l10 6-10 6z"/></svg>';

  const shownVideos = (wrap) => Array.from(wrap.querySelectorAll('video'))
    .filter((video) => getComputedStyle(video).display !== 'none');

  // Le bouton se place sur la diapositive ou le panneau, au-dessus du lien et du texte.
  const hostOf = (wrap) => wrap.closest('.slideshow__slide') || wrap.closest('.sb__panel') || wrap.parentElement;

  const setUp = (wrap) => {
    const end = wrap.dataset.end || 'loop';
    const tail = parseFloat(wrap.dataset.tail || '1');
    wrap.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.loop = end === 'loop';
      if (end === 'tail') {
        // On revient un peu avant la fin pour éviter l'arrêt d'une image de l'événement « ended ».
        const rewind = () => {
          video.currentTime = Math.max(0, video.duration - tail);
          video.play().catch(() => {});
        };
        video.addEventListener('timeupdate', () => {
          if (!video.paused && video.duration && video.currentTime >= video.duration - 0.12) rewind();
        });
        video.addEventListener('ended', () => {
          if (hostOf(wrap).dataset.videoPaused !== 'true') rewind();
        });
      }
    });
  };

  const start = (wrap, fromStart = true) => {
    if (hostOf(wrap).dataset.videoPaused === 'true') return;
    shownVideos(wrap).forEach((video) => {
      if (fromStart) video.currentTime = 0;
      video.play().catch(() => {});
    });
  };

  const stop = (wrap) => {
    wrap.querySelectorAll('video').forEach((video) => video.pause());
  };

  // Bouton pause / lecture : il suit l'état réel de la vidéo affichée. Si le navigateur refuse
  // la lecture automatique (mode économie d'énergie), il montre « lecture » pour la lancer.
  const addToggle = (wrap) => {
    const host = hostOf(wrap);
    let button = host.querySelector(':scope > .video-toggle');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'video-toggle';
      host.appendChild(button);
      const wraps = () => Array.from(host.querySelectorAll('[data-slide-video]'));
      const playing = () => wraps().some((w) => shownVideos(w).some((v) => !v.paused));
      button.refresh = () => {
        const isPlaying = playing();
        button.innerHTML = isPlaying ? ICON_PAUSE : ICON_PLAY;
        button.setAttribute('aria-label', isPlaying ? wrap.dataset.labelPause || 'Pause' : wrap.dataset.labelPlay || 'Play');
      };
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (playing()) {
          host.dataset.videoPaused = 'true';
          wraps().forEach(stop);
        } else {
          delete host.dataset.videoPaused;
          wraps().forEach((w) => start(w, false));
        }
      });
      button.refresh();
    }
    wrap.querySelectorAll('video').forEach((video) => {
      video.addEventListener('play', button.refresh);
      video.addEventListener('pause', button.refresh);
    });
  };

  // Hors diaporama : lecture quand la vidéo est à l'écran (un média caché n'est jamais « à l'écran »).
  const inView = new Set();
  const viewObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          inView.add(entry.target);
          start(entry.target, false);
        } else {
          inView.delete(entry.target);
          stop(entry.target);
        }
      });
    }, { rootMargin: '100px 0px' })
    : null;

  const watch = (wrap) => {
    if (viewObserver) viewObserver.observe(wrap); else start(wrap);
  };

  // Passage ordinateur / mobile (rotation d'une tablette) : une autre vidéo devient visible.
  window.matchMedia('(min-width: 768px)').addEventListener('change', () => {
    inView.forEach((wrap) => {
      stop(wrap);
      start(wrap, false);
    });
  });

  const init = () => {
    document.querySelectorAll('[data-slide-video]').forEach((wrap) => {
      if (wrap.dataset.slideVideoReady) return;
      wrap.dataset.slideVideoReady = 'true';
      setUp(wrap);
      addToggle(wrap);

      const slide = wrap.closest('.slideshow__slide');
      if (!slide) { watch(wrap); return; }

      let wasActive = slide.classList.contains('is-active');
      if (wasActive) start(wrap);

      // slideshow.js change de diapositive en déplaçant la classe is-active.
      new MutationObserver(() => {
        const isActive = slide.classList.contains('is-active');
        if (isActive && !wasActive) start(wrap);
        if (!isActive && wasActive) stop(wrap);
        wasActive = isActive;
      }).observe(slide, { attributes: true, attributeFilter: ['class'] });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Éditeur de thème : la section est rechargée à chaque réglage.
  document.addEventListener('shopify:section:load', init);
})();
